import React from 'react';
import { useParams, useLocation, useNavigate } from 'react-router-dom';
import { useQueryParam, NumberParam, StringParam } from 'use-query-params';
import { Box, Typography } from '@mui/material';
import makeStyles from '@mui/styles/makeStyles';
import GuessTheOrder from 'components/modules/Activities/GuessTheOrder';
import GuessWhere from 'components/modules/Activities/GuessWhere';
import GuessWho from 'components/modules/Activities/GuessWho';
import FeelingsMatch from 'components/modules/Activities/FeelingsMatch';
import SharedActivity from 'components/modules/Activities/SharedActivity';
import PracticeBook from './PracticeBook';
import ActivityProvider from 'state/providers/ActivityProvider';
import LiteRouteActivityProvider from 'state/providers/LiteRouteActivityProvider';
import tempGuessWhere from './tempImages/temp-guess-where.png';
import tempGuessWho from './tempImages/temp-guess-who.png';
import tempGuessTheOrder from './tempImages/temp-guess-the-order.png';
import tempFeelingsMatch from './tempImages/temp-feelings-match.png';
import tempGss from './tempImages/temp-gss.jpeg';
import { ActivityTypes } from 'types';
import LiteRouteActivity from './components/LiteRouteActivity';
import { AppNames } from '@monorepo/shared/src/types';
import ActivitiesLayout from './components/ActivitiesLayout';
import clsx from 'clsx';

const Activities: React.FC = () => {
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const { activityName } = useParams();
  const [assignmentId] = useQueryParam('assignmentId', NumberParam);
  const [activity] = useQueryParam('activity', StringParam);
  const [bookCode] = useQueryParam('bookCode', StringParam);
  const classes = styles();
  const currentBookCode = bookCode ?? 'MMAZLBA';
  const isTest = pathname.includes('test-activities');
  const alphabet = [
    'A',
    'B',
    'C',
    'D',
    'E',
    'F',
    'G',
    'H',
    'I',
    'J',
    'K',
    'L',
    'M',
    'N',
    'O',
    'P',
    'Q',
    'R',
    'S',
    'T',
    'U',
    'V',
    'W',
    'X',
    'Y',
    'Z',
  ];
  const setBookCode = (letter) => {
    navigate(`?bookCode=MMAZLB${letter}`, { replace: true });
  };

  const handleRouteNavDivClick = (route: string) => () => navigate(route);

  const displayActivity = (activity: string, assignmentId: number) => {
    switch (activity) {
      case ActivityTypes.GUESS_WHERE:
        return renderActivity(activity, <GuessWhere />, assignmentId);
      case ActivityTypes.GUESS_WHO:
        return renderActivity(activity, <GuessWho />, assignmentId);
      case ActivityTypes.GUESS_THE_ORDER:
        return renderActivity(activity, <GuessTheOrder />, assignmentId);
      case ActivityTypes.FEELINGS_MATCH:
        return renderActivity(activity, <FeelingsMatch />, assignmentId);
      case ActivityTypes.FIND_THE_LETTER:
      case ActivityTypes.FIND_THE_OBJECTS:
        return <SharedActivity activityType={activity} assignmentId={assignmentId} />;
      case ActivityTypes.PRACTICE_BOOK:
        return <PracticeBook assignmentId={assignmentId} />;
      case ActivityTypes.MATCH_IT_TRASH_IT:
      case ActivityTypes.STARTING_SOUND:
      case ActivityTypes.WDT:
        return renderLiteRouteActivity(activity, AppNames.GSS, <LiteRouteActivity />, assignmentId);
      default:
        return null;
    }
  };
  const LetterSelector = () => {
    const currentLetter = currentBookCode?.charAt(currentBookCode.length - 1) ?? 'A';
    return (
      <div>
        {alphabet.map((letter) => {
          return (
            <a
              className={clsx(
                classes.letterSelector,
                letter == currentLetter && classes.letterSelected
              )}
              key={letter}
              onClick={() => {
                setBookCode(letter);
              }}
            >
              {letter}
            </a>
          );
        })}
      </div>
    );
  };

  const displayActivitiesCards = () => {
    return (
      <>
        <div className={classes.gridContainer}>
          <div
            className={classes.gridItem}
            onClick={handleRouteNavDivClick(
              `/test-activities/guess-where?bookCode=${currentBookCode}`
            )}
          >
            <Box className={`${classes.tempImages}`}>
              <img alt="Temp Guess Where img" src={tempGuessWhere} />
            </Box>
            <Typography variant="h3" className={classes.text}>
              Guess Where
            </Typography>
          </div>
          <div
            className={classes.gridItem}
            onClick={handleRouteNavDivClick(
              `/test-activities/guess-who?bookCode=${currentBookCode}`
            )}
          >
            <Box className={`${classes.tempImages}`}>
              <img alt="Temp Guess Who img" src={tempGuessWho} />
            </Box>
            <Typography variant="h3" className={classes.text}>
              Guess Who
            </Typography>
          </div>
          <div
            className={classes.gridItem}
            onClick={handleRouteNavDivClick(
              `/test-activities/guess-the-order?bookCode=${currentBookCode}`
            )}
          >
            <Box className={`${classes.tempImages}`}>
              <img alt="Temp Guess The Order img" src={tempGuessTheOrder} />
            </Box>
            <Typography variant="h3" className={classes.text}>
              Guess The Order
            </Typography>
          </div>
          <div
            className={classes.gridItem}
            onClick={handleRouteNavDivClick(
              `/test-activities/feelings-match?bookCode=${currentBookCode}`
            )}
          >
            <Box className={`${classes.tempImages}`}>
              <img alt="Temp Feelings Match img" src={tempFeelingsMatch} />
            </Box>
            <Typography variant="h3" className={classes.text}>
              Feelings Match
            </Typography>
          </div>

          <div
            className={classes.gridItem}
            onClick={handleRouteNavDivClick(
              `/test-activities/match-it-trash-it?bookCode=${currentBookCode}`
            )}
          >
            <Box className={`${classes.tempImages}`}>
              <img alt="Temp Match it trash it img" src={tempGss} />
            </Box>
            <Typography variant="h1" className={classes.text}>
              Match It Trash It
            </Typography>
          </div>
          <div
            className={classes.gridItem}
            onClick={handleRouteNavDivClick(
              `/test-activities/starting-sound?bookCode=${currentBookCode}`
            )}
          >
            <Box className={`${classes.tempImages}`}>
              <img alt="Temp Starting sound img" src={tempGss} />
            </Box>
            <Typography variant="h1" className={classes.text}>
              Starting Sound
            </Typography>
          </div>
        </div>
      </>
    );
  };

  const menuProps = isTest ? { children: <LetterSelector /> } : undefined;

  const renderLiteRouteActivity = (
    activity,
    appName: AppNames,
    ActivityComponent,
    assignmentId
  ) => (
    <ActivitiesLayout assignmentId={assignmentId} menuProps={menuProps} showHelpButton={false}>
      <LiteRouteActivityProvider activity={activity} appName={appName}>
        {ActivityComponent}
      </LiteRouteActivityProvider>
    </ActivitiesLayout>
  );

  const renderActivity = (activity, ActivityComponent, assignmentId) => (
    <ActivitiesLayout assignmentId={assignmentId} menuProps={menuProps}>
      <ActivityProvider activity={activity}>{ActivityComponent}</ActivityProvider>
    </ActivitiesLayout>
  );

  return (
    <>
      {!activity && !activityName ? (
        <ActivitiesLayout assignmentId={assignmentId ?? 0} menuProps={menuProps}>
          {displayActivitiesCards()}
        </ActivitiesLayout>
      ) : (
        displayActivity(activity || activityName || '', assignmentId ?? 0)
      )}
    </>
  );
};

const styles = makeStyles(() => ({
  gridContainer: {
    display: 'grid',
    gridTemplateColumns: 'auto auto auto',
    position: 'relative',
    top: '3%',
  },
  gridItem: {
    margin: 'auto',
    padding: '20px',
    '&:hover': {
      cursor: 'pointer',
    },
  },
  guessWhereImage: {
    left: 'calc(75% - 350px)',
    position: 'absolute',
    top: '25%',
  },
  guessWhoImage: {
    left: 'calc(25% - 150px)',
    position: 'absolute',
    top: '25%',
  },
  tempImages: {
    maxWidth: '500px',
    '& > img': {
      boxShadow:
        '0px 2px 4px -1px rgba(0,0,0,0.2),0px 4px 5px 0px rgba(0,0,0,0.14),0px 1px 10px 0px rgba(0,0,0,0.12)',
      height: 'auto',
      width: '100%',
    },
    // minHeight: '300px',
  },
  text: {
    textAlign: 'center',
  },
  letterSelector: {
    margin: '.5rem',
    fontSize: '2rem',
    cursor: 'pointer',
  },
  letterSelected: {
    color: 'red',
  },
}));

export default Activities;
